/**
 * Image Upload Service
 * Handles all API calls related to uploading design images
 */

const API_URL = 'https://imageupdservice-tfjvhkdimj.cn-hongkong.fcapp.run/upload';

// Generate a per-reload client ID: yymmddhhmmss + 6 random digits
const generateClientId = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  const rnd = Math.floor(100000 + Math.random() * 900000); // 6 digits
  return `${yy}${MM}${dd}${hh}${mm}${ss}${rnd}`;
};

// Persist for this page session (module scope)
const CLIENT_ID = generateClientId();
export const getClientId = () => CLIENT_ID;

/**
 * Removes the "data:image/png;base64," part from base64 strings
 * @param {string} base64String - The full base64 string including data URI prefix
 * @returns {string} - Clean base64 string without prefix
 */
const cleanBase64String = (base64String) => {
  if (!base64String) return '';
  // Check if it has the data URI prefix
  if (base64String.startsWith('data:')) {
    const commaIndex = base64String.indexOf(',');
    if (commaIndex !== -1) {
      return base64String.substring(commaIndex + 1);
    }
  }
  return base64String;
};

/**
 * Upload design, preview, and texture images in a single API call
 * @param {string} designBase64 - Base64 string of the design image (masked design without product)
 * @param {string} previewBase64 - Base64 string of the preview image (full product with design)
 * @param {string} objectName - Name to use for the object (without design_ or preview_ prefix)
 * @param {string} userName - Optional user name (defaults to "DefaultUser")
 * @returns {Promise<Object>} - Object containing URLs to the uploaded images
 */
export const uploadBothImages = async (designBase64, previewBase64, objectName, userName = "DefaultUser", options = {}) => {
  try {
    // Clean the base64 strings by removing data URI prefixes
    const cleanPreview = cleanBase64String(previewBase64);
    const cleanDesign = cleanBase64String(designBase64);
    
    // Prepare the payload as expected by the API
    const payload = {
      design_Base64: cleanDesign,
      preview_Base64: cleanPreview,
      object_name: objectName,
      user_name: userName,
      client_id: options?.clientId || CLIENT_ID
    };
    
    console.log('Uploading both images with object name:', objectName, 'client_id:', payload.client_id);
    
    // Make the API call
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
    }
    
    // Parse the API response
    const result = await response.json();
    
    // The API returns design_url, preview_url, and potentially texture_url
    const designUrl = result.design_url || '';
    const previewUrl = result.preview_url || '';
    
    // Log for verification
    console.log('API returned design URL:', designUrl);
    console.log('API returned preview URL:', previewUrl);
    
    return {
      designUrl: designUrl,
      previewUrl: previewUrl,
      url: previewUrl, // For backwards compatibility
      objectName: objectName
    };
  } catch (error) {
    console.error('Error uploading both images:', error);
    throw error;
  }
};

